import React from 'react';
import { Slider } from '../../ui/slider';
import { Stepper } from './shared';
import { Users, Moon, Sparkles } from 'lucide-react';

const EVENT_TYPES = ['Wedding', 'Engagement', 'Reception', 'Haldi / Mehendi', 'Sangeet', 'Anniversary', 'Corporate Retreat', 'Other'];

const SliderRow = ({ icon: Icon, label, hint, value, onChange, max, step = 5, testId }) => (
  <div className="bg-white border border-[#E5DCCF] p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-[#C6A87C]" strokeWidth={1.5} />
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1A3626]">{label}</p>
          {hint && <p className="text-[11px] text-[#1A3626]/50 font-light mt-0.5">{hint}</p>}
        </div>
      </div>
      <input
        type="number"
        min={0}
        max={9999}
        value={value ?? 0}
        onChange={e => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="w-24 bg-[#FDFCF8] border border-[#E5DCCF] px-3 py-2 text-lg font-wvc-display text-[#0A1710] text-center tabular-nums focus:outline-none focus:border-[#C6A87C]"
        data-testid={testId ? `${testId}-input` : undefined}
      />
    </div>
    <div className="mt-4 px-1">
      <Slider
        value={[Math.min(value || 0, max)]}
        onValueChange={([v]) => onChange(v)}
        max={max}
        step={step}
        className="[&_[role=slider]]:bg-[#1A3626] [&_[role=slider]]:border-[#C6A87C] [&_.bg-primary]:bg-[#C6A87C]"
        data-testid={testId ? `${testId}-slider` : undefined}
      />
      <div className="flex justify-between text-[9px] text-[#1A3626]/30 font-bold mt-1.5 tabular-nums">
        <span>0</span><span>{max}+</span>
      </div>
    </div>
  </div>
);

const guestCompliment = (guests) => {
  if (!guests) return 'Slide or type — every guest counts.';
  if (guests < 50) return 'An intimate gathering — often the most heartfelt celebrations of all.';
  if (guests < 150) return 'A warm, close-knit crowd — the venue will feel just right.';
  if (guests < 400) return 'A proper celebration — friends, family and plenty of dancing.';
  return 'A grand affair — the whole venue will come alive for you.';
};

export const StepGuests = ({ config, basics, setBasics }) => {
  const perHeadPkgs = (config.venue_packages || []).filter(p => Number(p.included_guests) > 0 && Number(p.extra_guest_charge) > 0);

  return (
    <div className="space-y-6" data-testid="calc-step-guests">
      <div>
        <p className="font-wvc-display text-2xl md:text-3xl font-light text-[#0A1710] tracking-tight">
          Wonderful. Now, who&rsquo;s coming?
        </p>
        <p className="text-[#1A3626]/60 text-sm font-light mt-2" data-testid="calc-guests-compliment">
          {guestCompliment(Number(basics.guests))}
        </p>
      </div>

      <div className="space-y-4">
        <SliderRow
          icon={Users}
          label="Event guests"
          hint="The floating crowd across your functions"
          value={Number(basics.guests) || 0}
          onChange={v => setBasics({ ...basics, guests: v })}
          max={1000}
          step={10}
          testId="calc-guests"
        />
        <SliderRow
          icon={Moon}
          label="Staying overnight"
          hint="Guests who&rsquo;ll wake up at the venue"
          value={Number(basics.overnight_guests) || 0}
          onChange={v => setBasics({ ...basics, overnight_guests: v })}
          max={300}
          step={5}
          testId="calc-overnight"
        />
      </div>

      {perHeadPkgs.length > 0 && (
        <div className="border border-[#C6A87C]/40 bg-[#C6A87C]/8 p-4 flex items-start gap-3" data-testid="calc-perhead-note">
          <Sparkles className="w-4 h-4 text-[#C6A87C] mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="text-xs text-[#1A3626]/70 font-light leading-relaxed">
            Good to know: {perHeadPkgs.map((p, i) => (
              <span key={p.id || i}>
                <b className="font-semibold">{p.name}</b> includes {Number(p.included_guests)} guests, then ₹{Number(p.extra_guest_charge).toLocaleString('en-IN')} per additional guest{i < perHeadPkgs.length - 1 ? '; ' : '. '}
              </span>
            ))}
            Your estimate adjusts automatically as you slide.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-[#E5DCCF]">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60 mb-1.5 pt-4">Children</label>
          <Stepper value={basics.children} onChange={v => setBasics({ ...basics, children: v })} min={0} testId="calc-children" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60 mb-1.5 pt-4">Type of event</label>
          <select
            value={basics.event_type}
            onChange={e => setBasics({ ...basics, event_type: e.target.value })}
            className="w-full bg-white border border-[#E5DCCF] px-3 py-2.5 text-sm text-[#0A1710] focus:outline-none focus:border-[#C6A87C]"
            data-testid="calc-event-type"
          >
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60 mb-1.5 pt-4">Number of functions</label>
          <Stepper value={basics.functions_count} onChange={v => setBasics({ ...basics, functions_count: v })} min={1} max={12} testId="calc-functions" />
        </div>
      </div>
    </div>
  );
};
